import { memo, useState, useEffect } from 'react';
import logger from '~/utils/logger';
import { FormField, TextareaAutosize, InputWithDropdown } from '~/components/ui';
import { cn, removeFocusRings } from '~/utils';
import { Search } from 'lucide-react';
import { useAuthContext } from '~/hooks/AuthContext';
import { useMediaQuery } from '~/hooks';
import usePersistedState from '~/hooks/usePersistedState';
import './tools.css';

const GPTResearcher = () => {
  // Get the token from the auth context
  const { token } = useAuthContext();
  
  // State for research
  const [isResearching, setIsResearching] = useState(false);
  const [researchResults, setResearchResults] = usePersistedState<string | null>('gptResearcher.results', null);
  const [error, setError] = useState<string | null>(null);
  const [downloadLinks, setDownloadLinks] = useState<{type: string, url: string, filename: string}[]>([]);
  
  // State for form values
  const [researchTopic, setResearchTopic] = usePersistedState<string>('gptResearcher.topic', '');
  const [reportType, setReportType] = usePersistedState<string>('gptResearcher.reportType', 'summary');
  const [tone, setTone] = usePersistedState<string>('gptResearcher.tone', 'objective');
  const [source, setSource] = usePersistedState<string>('gptResearcher.source', 'web');

  const isSmallScreen = useMediaQuery('(max-width: 767px)');

  // Report type options
  const reportTypeOptions = [
    { value: 'summary', label: 'Summary - Short and fast (~2 min)' },
    { value: 'research-report', label: 'Research Report - Detailed analysis (~5 min)' },
    { value: 'comprehensive', label: 'Comprehensive - In-depth exploration (~10 min)' },
    { value: 'deep', label: 'Deep - Exhaustive in-depth analysis (~15 min)' },
  ];

  // Tone options
  const toneOptions = [
    { value: 'objective', label: 'Objective - impartial and unbiassed presentation of facts and findings' },
    { value: 'analytical', label: 'Analytical - focused on breaking down complex topics' },
    { value: 'critical', label: 'Critical - evaluating strengths and weaknesses' },
    { value: 'persuasive', label: 'Persuasive - convincing argument for a position' },
  ];

  // Source options
  const sourceOptions = [
    { value: 'web', label: 'The Web' },
    { value: 'academic', label: 'Academic Sources' },
    { value: 'news', label: 'News Sources' },
    { value: 'books', label: 'Books and Literature' },
  ];
  
  // Check authentication token on component mount
  useEffect(() => {
    logger.log('GPTResearcher', 'Auth token on mount:', token ? 'Present (length: ' + token.length + ')' : 'Missing');
    
    // Check token format - should be a JWT token (format: xxx.yyy.zzz)
    if (token) {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        logger.warn('GPTResearcher', 'Token does not appear to be a valid JWT (should have 3 parts separated by dots)');
      } else {
        logger.log('GPTResearcher', 'Token appears to be a valid JWT format');
        
        // Only attempt to decode the JWT payload in the browser environment
        if (typeof window !== 'undefined' && typeof atob === 'function') {
          try {
            // Safe decoding of the JWT payload (middle part)
            const base64Url = tokenParts[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const payload = JSON.parse(atob(base64));
            logger.log('GPTResearcher', 'Token payload:', payload);
            
            if (payload.exp) {
              const expirationDate = new Date(payload.exp * 1000);
              const now = new Date();
              logger.log('GPTResearcher', 'Token expiration:', {
                expirationDate: expirationDate.toISOString(),
                now: now.toISOString(),
                isExpired: expirationDate < now,
              });
            }
          } catch (e) {
            logger.error('GPTResearcher', 'Error decoding token payload:', e);
          }
        }
      }
    }
  }, [token]);

  // Initialize default values
  useEffect(() => {
    // Force re-render with default values if they're not set correctly
    if (!reportTypeOptions.find(option => option.value === reportType)?.label) {
      setReportType('summary');
    }
    if (!toneOptions.find(option => option.value === tone)?.label) {
      setTone('objective');
    }
    if (!sourceOptions.find(option => option.value === source)?.label) {
      setSource('web');
    }
    
    // Log current values for debugging
    logger.log('GPTResearcher', 'Current values:', { 
      reportType, 
      tone, 
      source,
      reportTypeLabel: reportTypeOptions.find(option => option.value === reportType)?.label,
      toneLabel: toneOptions.find(option => option.value === tone)?.label,
      sourceLabel: sourceOptions.find(option => option.value === source)?.label,
    });
  }, [reportType, tone, source, reportTypeOptions, toneOptions, sourceOptions, setReportType, setTone, setSource]);

  // Handle form value changes
  const handleResearchTopicChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    logger.log('GPTResearcher', 'Research topic changed:', e.target.value);
    setResearchTopic(e.target.value);
  };

  const handleReportTypeChange = (value: string) => {
    logger.log('GPTResearcher', 'Report type changed:', value);
    setReportType(value);
  };

  const handleToneChange = (value: string) => {
    logger.log('GPTResearcher', 'Tone changed:', value);
    setTone(value);
  };

  const handleSourceChange = (value: string) => {
    logger.log('GPTResearcher', 'Source changed:', value);
    setSource(value);
  };

  const handleResearch = async () => {
    logger.log('GPTResearcher', 'Research button clicked');
    setError(null);
    setResearchResults(null);
    setDownloadLinks([]);

    if (!researchTopic.trim()) {
      setError('Please enter a research topic');
      return;
    }
    
    // Check if user is authenticated
    if (!token) {
      setError('You must be logged in to use this feature. Please log in and try again.');
      return;
    }

    try {
      setIsResearching(true);

      // Extract values from dropdown objects if needed
      const getStringValue = (value: unknown): string => {
        if (typeof value === 'string') {
          return value;
        }
        if (value && typeof value === 'object' && value !== null) {
          // Check if the object has a 'value' property that is a string
          const obj = value as Record<string, unknown>;
          if ('value' in obj && typeof obj.value === 'string') {
            return obj.value;
          }
        }
        return String(value);
      };

      const payload = {
        task: researchTopic,
        report_type: getStringValue(reportType),
        tone: getStringValue(tone).charAt(0).toUpperCase() + getStringValue(tone).slice(1), // Capitalize first letter
        source: getStringValue(source),
      };

      // Log detailed request information for debugging
      logger.log('GPTResearcher', 'Auth token:', token ? 'Present (length: ' + token.length + ')' : 'Missing');
      
      // Check token format - should be a JWT token (format: xxx.yyy.zzz)
      if (token) {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          logger.warn('GPTResearcher', 'Token does not appear to be a valid JWT (should have 3 parts separated by dots)');
        } else {
          logger.log('GPTResearcher', 'Token appears to be a valid JWT format');
        }
      }
      
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      
      logger.log(
        'GPTResearcher',
        'Sending request to API:',
        {
          url: '/api/tools/gpt-researcher',
          method: 'POST',
          headers,
          payload,
          payloadJSON: JSON.stringify(payload),
        },
      );

      const response = await fetch('/api/tools/gpt-researcher', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to complete research');
      }

      const data = await response.json();
      logger.log('GPTResearcher', 'Research completed successfully', data);

      if (data.status === 'completed' && data.files) {
        // Process file links
        const links: Array<{type: string, url: string, filename: string}> = [];
        for (const fileType in data.files) {
          const fileData = data.files[fileType];
          links.push({
            type: fileType,
            url: fileData.url,
            filename: fileData.filename,
          });
        }
        setDownloadLinks(links);
        
        // Set a success message
        setResearchResults('Your research report is ready! You can download it using the links below.');
      } else {
        setResearchResults('Research completed, but no files were returned.');
      }
    } catch (error) {
      logger.error('GPTResearcher', 'Error during research:', error);
      setError(
        error instanceof Error ? error.message : 'Failed to complete research',
      );
    } finally {
      setIsResearching(false);
    }
  };

  return (
    <div className="relative flex h-full w-full flex-col items-center overflow-y-auto bg-white px-6 pb-3 pt-6 text-[#0F172A] md:px-8 md:pt-[80px] dark:bg-[#0F172A] dark:text-white">
      {/* Header */}
      <div className="mb-8 flex flex-col items-center md:px-16">
        <div className="mb-4 flex items-center gap-2">
          <Search
            size={isSmallScreen ? 36 : 48}
            className="text-[#0F172A] dark:text-white"
          />
          <h1 className="text-[28px] font-medium leading-[36px] tracking-[-.225px] text-[#0F172A] md:ml-4 md:text-[48px] md:font-bold md:leading-normal md:tracking-[-.576px] dark:text-white">
            GPT Researcher
          </h1>
        </div>
        <p className="font-base max-w-2xl text-center text-[12px] font-medium leading-[14px] text-[#0F172A] md:text-[18px] md:leading-[24px] dark:text-white">
          Generate detailed research reports on any topic
        </p>
      </div>

      {/* Research Form */}
      <div className="mt-8 flex w-full max-w-5xl flex-col space-y-8">
        {!token && (
          <div className="mb-4 rounded-md bg-yellow-100 p-3 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            <strong>Note:</strong> You must be logged in to use this feature. Please log in to generate research reports.
          </div>
        )}
        <div className="mx-auto flex flex-col items-center justify-center space-y-4 w-full">
          <FormField label="What would you like me to research next?" labelClassName='text-[18px] font-medium leading-[24px] text-[#0F172A] dark:text-white'>
            <div className="relative flex w-full justify-center">
              <div
                className="relative flex w-full overflow-hidden rounded-lg border border-transparent"
                style={{
                  background:
                    'linear-gradient(91.969deg, #F214A1 0%, #BC0278 100%)',
                }}
              >
                <div
                  className="relative flex h-full w-full flex-grow flex-row items-center overflow-hidden bg-[#F2F2F2] p-1 dark:bg-[#1c2846]"
                  style={{
                    backgroundClip: 'padding-box',
                  }}
                >
                  <TextareaAutosize
                    value={researchTopic}
                    onChange={(e) => handleResearchTopicChange(e)}
                    placeholder="Enter Here"
                    id="research-topic-input"
                    tabIndex={0}
                    data-testid="research-topic-input"
                    style={{ height: 44 }}
                    rows={1}
                    className={cn(
                      'm-0 max-h-[65vh] w-full resize-none bg-[#F2F2F2] py-[13px] text-[#0F172A] placeholder:text-[#0F172A/60] md:max-h-[75vh] md:py-3.5 dark:bg-[#1c2846] dark:text-white dark:placeholder:text-white/60 [&:has(textarea:focus)]:shadow-[0_2px_6px_rgba(0,0,0,.05)]',
                      'pl-4 pr-4',
                      removeFocusRings,
                    )}
                  />
                </div>
              </div>
            </div>
          </FormField>

          <FormField label="What type of report would you like me to generate?" labelClassName='text-[18px] font-medium leading-[24px] text-[#0F172A] dark:text-white'>
            <InputWithDropdown
              value={reportTypeOptions.find(option => option.value === reportType)?.label || ''}
              onChange={(e) => {
                const selectedOption = reportTypeOptions.find(option => option.label === e.target.value);
                if (selectedOption) {
                  handleReportTypeChange(selectedOption.value);
                }
              }}
              options={reportTypeOptions.map(option => option.label)}
              className="w-full"
              readOnly={true}
            />
          </FormField>

          <FormField label="In which tone would you like the report to be generated?" labelClassName='text-[18px] font-medium leading-[24px] text-[#0F172A] dark:text-white'>
            <InputWithDropdown
              value={toneOptions.find(option => option.value === tone)?.label || ''}
              onChange={(e) => {
                const selectedOption = toneOptions.find(option => option.label === e.target.value);
                if (selectedOption) {
                  handleToneChange(selectedOption.value);
                }
              }}
              options={toneOptions.map(option => option.label)}
              className="w-full"
              readOnly={true}
            />
          </FormField>

          <FormField label="What Sources would you like me to research from?" labelClassName='text-[18px] font-medium leading-[24px] text-[#0F172A] dark:text-white'>
            <InputWithDropdown
              value={sourceOptions.find(option => option.value === source)?.label || ''}
              onChange={(e) => {
                const selectedOption = sourceOptions.find(option => option.label === e.target.value);
                if (selectedOption) {
                  handleSourceChange(selectedOption.value);
                }
              }}
              options={sourceOptions.map(option => option.label)}
              className="w-full"
              readOnly={true}
            />
          </FormField>
        </div>
        
        <div className="w-full flex justify-center mt-4">
          <button
            className={cn('button-gradient rounded-lg p-2 text-white transition-colors md:px-6 md:py-3 flex items-center justify-center min-w-[120px]', isSmallScreen ? 'w-full' : 'w-auto')}
            onClick={handleResearch}
            disabled={isResearching || !researchTopic.trim()}
          >
            {isResearching && (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-b-transparent border-white"></div>
                Researching...
              </>
            )}
            {!isResearching && 'Research'}
          </button>
        </div>
        
        {error && (
          <div className="mt-4 rounded-md bg-red-100 p-3 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Agent Output */}
        {(researchResults || downloadLinks.length > 0) && (
          <div className="w-full">
            <h2 className="text-[24px] font-bold mb-4">Agent Output</h2>
            <p className="mb-4">An agent tailored specifically to your task will be generated to provide the most precise and relevant research results.</p>
            
            <div className="border border-gray-300 rounded-lg p-6 dark:border-gray-700">
              {researchResults && <p className="mb-4">{researchResults}</p>}
              
              {downloadLinks.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-lg font-medium mb-2">Download Files:</h3>
                  <ul className="space-y-2">
                    {downloadLinks.map((link, index) => (
                      <li key={index}>
                        <a 
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {link.filename}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="mt-6 text-center">
                <p>
                  <a href="/" className="text-blue-600 hover:underline dark:text-blue-400 mx-2">Homepage</a> | 
                  <a href="https://github.com" className="text-blue-600 hover:underline dark:text-blue-400 mx-2">GitHub</a> | 
                  <a href="https://discord.com" className="text-blue-600 hover:underline dark:text-blue-400 mx-2">Discord</a>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(GPTResearcher);
